import { Calendar, Clock, ChevronRight, User } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { Guide } from "@/data/guides";
import { formatDate, guideKategorien } from "@/data/guides";

interface GuideCardProps {
  guide: Guide;
  onNavigate: (path: string) => void;
}

export function GuideCard({ guide, onNavigate }: GuideCardProps) {
  const kategorie = guideKategorien.find(k => k.id === guide.kategorie);

  return (
    <Card
      className="group overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer flex flex-col"
      onClick={() => onNavigate(`/ratgeber/${guide.slug}`)}
    >
      {/* Image */}
      <div className="relative aspect-[16/9] bg-muted overflow-hidden">
        <img
          src={guide.bild}
          alt={guide.titel}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          loading="lazy"
        />
        {/* Kategorie Badge */}
        {kategorie && (
          <div className="absolute top-3 left-3">
            <Badge variant="secondary" className="bg-background/90 backdrop-blur">
              {kategorie.name}
            </Badge>
          </div>
        )}
      </div>

      {/* Content */}
      <CardContent className="p-4 flex-1 flex flex-col">
        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {guide.tags.slice(0, 3).map(tag => (
            <Badge key={tag} variant="outline" className="text-xs">
              {tag}
            </Badge>
          ))}
        </div>

        {/* Title */}
        <h3 className="font-semibold text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">
          {guide.titel}
        </h3>

        {/* Description */}
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2 flex-1">
          {guide.beschreibung}
        </p>

        {/* Meta */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
          <div className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            <span>{formatDate(guide.datum)}</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            <span>{guide.lesezeit} Min.</span>
          </div>
        </div>

        {/* Author */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4 pb-4 border-b">
          <User className="h-4 w-4" />
          <div>
            <div className="font-medium text-foreground">{guide.autor.name}</div>
            <div className="text-xs">{guide.autor.rolle}</div>
          </div>
        </div>

        {/* Button */}
        <Button
          variant="ghost"
          className="w-full group/btn border border-border hover:border-foreground transition-all duration-300"
          onClick={e => {
            e.stopPropagation();
            onNavigate(`/ratgeber/${guide.slug}`);
          }}
        >
          Artikel lesen
          <ChevronRight className="h-4 w-4 ml-1 transition-transform group-hover/btn:translate-x-1" />
        </Button>
      </CardContent>
    </Card>
  );
}
